import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextType>({
  theme: "system",
  setTheme: () => {},
  resolvedTheme: "light",
});

function getSystemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem("theme") as Theme) || "system"
  );

  const resolvedTheme = theme === "system" ? getSystemTheme() : theme;

  // Carregar tema do banco quando o usuário logar (dispara apenas quando user.id muda)
  useEffect(() => {
    if (!user?.id) return;

    supabase
      .from("profiles")
      .select("theme")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data?.theme) {
          const dbTheme = data.theme as Theme;
          setThemeState(dbTheme);
          localStorage.setItem("theme", dbTheme);
        }
      });
  }, [user?.id]);

  const setTheme = useCallback(
    (t: Theme) => {
      setThemeState(t);
      localStorage.setItem("theme", t);

      // Persistir no banco se o usuário estiver autenticado
      if (user?.id) {
        supabase
          .from("profiles")
          .update({ theme: t } as any)
          .eq("id", user.id)
          .then(() => { /* persistido */ })
          .catch(() => { /* não-crítico — tema já salvo localmente */ });
      }
    },
    [user?.id]
  );

  // Aplicar classe de tema no <html>
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  // Reagir a mudanças na preferência do sistema quando tema = "system"
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const root = document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(getSystemTheme());
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
